import { forumCommentPolicy } from "@/policies";
import { ForumCommentModel } from "@/models";
import { pendingForumCommentsStorage } from "@/services/storage";

(function (angular) {
  angular.module("app").directive("forumThreadReply", forumThreadReply);

  forumThreadReply.$inject = [
    "$q",
    "$rootScope",
    "modalService",
    "ForumLikesApi",
    "ForumRepliesApi",
    "thisUser",
    "mimetypesPayload",
    "alerts",
    "UploadsApi",
    "thisSiteSettings",
  ];

  function forumThreadReply(
    $q,
    $rootScope,
    modalService,
    ForumLikesApi,
    ForumRepliesApi,
    thisUser,
    mimetypesPayload,
    alerts,
    UploadsApi,
    thisSiteSettings,
  ) {
    const link = function ($scope) {
      // properties
      $scope.policyChecks = getPolicyChecks();
      $scope.thisSiteSettings = thisSiteSettings;
      $scope.input = {};
      $scope.thread = $scope.thread || { thread: "", body: "" };
      $scope.reply = $scope.reply || {
        body: "",
        entities: [],
        uploads: [],
        upload_files: {
          images: [],
          audio: [],
          documents: [],
          videos: [],
        },
      };

      $scope.isDeleting = false;
      $scope.isLoading = false;
      $scope.isEditing = $scope.isEditing || false;
      $scope.isExpanded = false;
      $scope.user = thisUser;
      $scope.fileUploadOptions = $scope.fileUploadOptions || {
        filePattern: "*",
        maxFilesize: 256 * 1024 * 1024, // 256 MB
        dragOverClass: "dragging",
      };
      $scope.isExpanded = false;

      // methods
      $scope.deleteReply = deleteReply;
      $scope.deleteReplyApi = deleteReplyApi;
      $scope.deleteReplyCancel = deleteReplyCancel;
      $scope.editReply = editReply;
      $scope.editReplyApi = editReplyApi;
      $scope.editReplyCancel = editReplyCancel;
      $scope.formatBody = formatBody;
      $scope.limitTo = limitTo;
      $scope.saveReply = saveReply;
      $scope.removeFile = removeFile;
      $scope.isSaving = false;
      $scope.toggleLike = toggleLike;
      $scope.getLikes = getLikes;
      $scope.onFileSelected = onFileSelected;
      $scope.onFileSelectedError = onFileSelectedError;
      $scope.setExpanded = setExpanded;

      async function bootstrap(): Promise<void> {
        // The parent entity this thread belongs to
        const parentType = $scope.parentReply ? "comment" : "thread";
        const parentId =
          parentType === "comment" ? $scope.parentReply.id : $scope.thread.id;

        $scope.$watch(
          "reply",
          () => {
            $scope.policyChecks = getPolicyChecks();

            // Sync comment changes to storage
            if ($scope.isEditing) {
              pendingForumCommentsStorage.setComment(
                parentType,
                parentId,
                $scope.reply,
              );
            }
          },
          true,
        );

        // Rehydrate pending comment from storage
        if ($scope.isEditing) {
          const reply = await pendingForumCommentsStorage.getComment(
            parentType,
            parentId,
          );
          $scope.reply = { ...reply }; // clone the object or Angular will sync all references to it
        }
      }

      /**
       * Gets an object of cached policy checks.
       *
       * @returns {Record<string, boolean>}
       */
      function getPolicyChecks(): Record<string, boolean> {
        try {
          return {
            canDelete: forumCommentPolicy.canDelete(
              new ForumCommentModel($scope.reply),
            ),
            canEdit: forumCommentPolicy.canEdit(
              new ForumCommentModel($scope.reply),
            ),
          };
        } catch (err) {
          console.warn(err);
        }
      }

      /**
       * Set the isExpanded variable.
       *
       * @param {Boolean} expanded
       */
      function setExpanded(expanded) {
        $scope.isExpanded = expanded;
      }

      /**
       * Show a delete reply "modal".
       *
       * @param  {Event=} clickEvent
       * @return {void}
       */
      function deleteReply(clickEvent?: Event) {
        if (clickEvent) {
          clickEvent.preventDefault();
        }

        $scope.isDeleting = true;
      }

      /**
       * Delete the reply when the yes button is clicked.
       *
       * @param  {Integer} id
       * @return {void}
       */
      function deleteReplyApi(id) {
        return $q(function (resolve) {
          ForumRepliesApi.delete({ replyId: id }, function () {
            $rootScope.$emit(
              "forum:updateReplies",
              $scope.reply.forum_thread_id,
            );
            resolve();
          });
        });
      }

      /**
       * Cancel deleting the reply when the no button is clicked.
       *
       * @param  {Integer} id
       * @return {void}
       */
      function deleteReplyCancel() {
        $scope.isDeleting = false;
      }

      /**
       * Show a delete reply "modal".
       *
       * @param  {Event=} clickEvent
       * @return {void}
       */
      function editReply(clickEvent?: Event) {
        if (clickEvent) {
          clickEvent.preventDefault();
        }

        $scope.isEditing = true;
      }

      /**
       * Delete the reply when the yes button is clicked.
       *
       * @param  {Integer} id
       * @return {void}
       */
      function editReplyApi(id) {
        return $q(function (resolve) {
          ForumRepliesApi.delete({ replyId: id }, function () {
            $rootScope.$emit(
              "forum:updateReplies",
              $scope.reply.forum_thread_id,
            );
            resolve();
          });
        });
      }

      /**
       * Cancel deleting the reply when the no button is clicked.
       *
       * @param  {Integer} id
       * @return {void}
       */
      function editReplyCancel() {
        $scope.isEditing = false;
      }

      /**
       * Format the body - add links, create breaks where newlines are.
       *
       * @return {String}
       */
      function formatBody(): string {
        let formatted = new ForumCommentModel($scope.reply).expandEntities();
        formatted = formatted + "\r\n"; // for some reason the last line is cut off without adding this to the end
        formatted = formatted.replace(
          /(https?|ftps?)\:\/\/[a-zA-Z0-9\-\.]+\.[a-zA-Z]{2,3}(\/\S*)?/g,
          '<a href="$&">$&</a>',
        );
        formatted = formatted.replace(/(\r\n|\n|\r)/gm, "<br>");
        return formatted;
      }

      /**
       * Nicely limit a string of text, with optional formatting. This is
       * useful for if, in the future, we wanted to render the html and then
       * have us essentially limit the text, preserving formatting.
       *
       * @param  {String} text
       * @param  {Number} limitTo
       * @return {String}
       */
      function limitTo(text, entities, limitTo) {
        // Make substitutions to make the text nicer (adding links, creating breaks).
        const formatted = formatBody();
        // Essentially what we're doing here is being awfully nice to the
        // rendering engine and telling it to end our tags for us.
        var $el = document.createElement("div");
        $el.innerHTML =
          formatted.substr(0, limitTo) +
          (formatted.length > limitTo ? "..." : "");
        $el.innerHTML = $el.innerHTML.replace(/^(.*)\<\/?.+$/, "$1");
        return (
          $el.innerHTML +
          (formatted.length > limitTo
            ? '<br><a class="read-more-link" href="/forum/' +
              $scope.category.slug +
              "/" +
              $scope.thread.slug +
              '">Read more &raquo;</a>'
            : "")
        );
      }

      function removeFile(category, file) {
        $scope.reply.upload_files[category].splice(
          $scope.reply.upload_files[category].indexOf(file),
          1,
        );
        UploadsApi.removeFile(file.path, file.id);
      }

      /**
       * Create or update a reply.
       *
       * @return {Promise<void>}
       */
      function saveReply(): Promise<void> {
        $scope.isSaving = true;

        const body = {
          body: $scope.reply.body,
          entities: $scope.reply.entities.map((entity) => {
            delete entity.entity;
            return entity;
          }),
          parent_id: $scope.parentReply ? $scope.parentReply.id : undefined,
          uploads: $scope.reply.uploads || [],
          upload_files: $scope.reply.upload_files || {
            images: [],
            audio: [],
            documents: [],
            videos: [],
          },
          private: $scope.thread.private,
        };

        return new Promise((resolve, reject) => {
          if ($scope.reply.id) {
            ForumRepliesApi.update(
              {
                replyId: $scope.reply.id,
              },
              {
                ...body,
                forum_thread_id: $scope.reply.forum_thread_id,
              },
              () => {
                $scope.isSaving = false;
                $rootScope.$emit(
                  "forum:updateReplies",
                  $scope.reply.forum_thread_id,
                );
                resolve();
              },
              () => {
                $scope.isSaving = false;
                reject;
              },
            );
          } else {
            ForumRepliesApi.save(
              {},
              {
                ...body,
                forum_thread_id: $scope.thread.id,
              },
              () => {
                $scope.reply = {
                  body: "",
                  entities: [],
                  uploads: [],
                  upload_files: {
                    images: [],
                    audio: [],
                    documents: [],
                    videos: [],
                  },
                };
                $scope.isSaving = false;
                $rootScope.$emit("forum:updateReplies", $scope.thread.id);
                resolve();
              },
              () => {
                $scope.isSaving = false;
                reject;
              },
            );
          }
        });
      }

      /**
       * Toggle the liked status of the thread.
       *
       * @return {void}
       */
      function toggleLike() {
        if (thisUser && thisUser.id) {
          if ($scope.reply.user_liked) {
            ForumLikesApi.delete(
              {
                likeId: $scope.reply.user_liked,
              },
              {},
              function () {
                $scope.reply.user_liked = 0;
                $scope.reply.total_likes -= 1;
              },
            );
          } else {
            ForumLikesApi.save(
              {},
              {
                forum_object_id: $scope.reply.id,
                forum_object_type: "reply",
              },
              function (like) {
                $scope.reply.user_liked = like.data.id;
                $scope.reply.total_likes += 1;
              },
            );
          }
        }
      }

      /**
       * Toggle the liked status of the thread.
       *
       * @return {void}
       */
      function getLikes(replyId) {
        ForumLikesApi.query(
          {
            forum_object_id: replyId,
            forum_object_type: "reply",
          },
          function (likes) {
            modalService
              .open({
                templateUrl: "modals/likes.html",
                controller: "likesController",
                controllerAs: "vm",
                windowClass: "likes",
                resolve: {
                  likes: function () {
                    return likes;
                  },
                },
              })
              .result.then(function (resizedImage) {
                $scope.onSelect(resizedImage);
              });
          },
        );
      }

      /**
       * Caches the file and uploads to the API
       *
       * @param  {String} base64Data - file converted to base64
       * @param  {Object} file   - brower file object
       * @return {Void}
       */
      function onFileSelected(base64Data, file) {
        const filePlaceholder = {
          client_filename: file.name,
          isUploading: true,
          uploadProgress: 0,
        };

        // check file type is valid
        const acceptedFileTypes = Object.keys(mimetypesPayload);
        if (acceptedFileTypes.indexOf(file.type) === -1) {
          return alerts.emit({
            type: "warning",
            title: "File Error",
            message: "Unsupported file type",
          });
        }

        // determine category file should be placed into
        const fileType = (mimetypesPayload[file.type] || { type: "document" })
          .type;
        const fileTypeCategoryMap = {
          image: "images",
          audio: "audio",
          document: "documents",
          csv: "documents",
          video: "videos",
        };
        // save reference to the category array that will hold this file
        const fileCategory =
          $scope.reply.upload_files[fileTypeCategoryMap[fileType]];

        if (!fileCategory) {
          return alerts.emit({
            theme: "error",
            title: `File type "${fileType}" is not supported`,
          });
        }

        // add file placeholder to resource to update the view
        fileCategory.push(filePlaceholder);

        // get index of file placeholder
        const fileObjectIndex = fileCategory.length - 1;

        // upload the file
        UploadsApi.uploadFile(file, "forum", true).then(
          function handleUploadFileSuccess(response) {
            delete fileCategory[fileObjectIndex].isUploading;
            // save file ID to resource
            $scope.reply.uploads.push(response.id);
            // update file placeholder with complete data
            fileCategory[fileObjectIndex] = response;
          },
          function handleUploadFileError() {
            fileCategory.splice(fileObjectIndex, 1);
          },
          function handleUploadProgress(progress) {
            fileCategory[fileObjectIndex].uploadProgress = progress;
          },
        );
      }

      function onFileSelectedError(error) {
        alerts.emit({
          theme: "error",
          title: "This file could not be uploaded",
          message: error.image,
        });
      }

      // init controller
      bootstrap();
    };

    return {
      restrict: "E",
      templateUrl: "directives/forumThreadReply.html",
      transclude: true,
      link,
      scope: {
        isNestedReply: "=?",
        parentReply: "=?",
        isEditing: "=?",
        reply: "=?",
        thread: "=?",
      },
    };
  }
})(angular);
